import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Collapse,
  Grid,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { useDispatch, useSelector } from "react-redux";
import AttendanceVeiwController from "../../redux/actions/AttendenceApiAction";
import AuthViewController from "../../redux/actions/AuthViewController";
import { getUserInfoFromToken } from "../../utils/authUtils";
import { BRAND } from "../../components/auth/authTheme";
import MobileMonthYearFilters from "../../components/mobile/MobileMonthYearFilters";
import "../../styles/attendance.css";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = BRAND.bg;

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "14px",
  border: `1px solid ${BRAND.border}`,
  boxShadow: "0 2px 12px rgba(26, 43, 74, 0.05)",
};

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString("default", { month: "long" }),
  shortLabel: new Date(2000, i, 1).toLocaleString("default", { month: "short" }),
}));

const legendData = [
  { code: "P", label: "Present", className: "present" },
  { code: "A", label: "Absent", className: "absent" },
  { code: "O", label: "Off Day", className: "offday" },
  { code: "R", label: "Rest Day", className: "restday" },
  { code: "L", label: "Leave", className: "leave" },
  { code: "H", label: "Holiday", className: "holiday" },
  { code: "½", label: "Half Day", className: "halfday" },
  { code: "OD", label: "On Duty", className: "onduty" },
  { code: "E", label: "Early Logout", className: "early" },
];

const STATUS_COPY = {
  A: () => ({ title: "Absent", sub: "No clock-in recorded for this day." }),
  O: () => ({ title: "Off Day", sub: "Scheduled weekly off." }),
  R: () => ({ title: "Rest Day", sub: "Scheduled rest day." }),
  L: () => ({ title: "On Leave", sub: "Approved leave for this day." }),
  P: () => ({ title: "Present", sub: "Attendance recorded for this day." }),
  H: (_n, detail) => ({
    title: detail || "Holiday",
    sub: "Company holiday — no attendance required.",
  }),
  E: () => ({ title: "Early Logout", sub: "Logged out before shift end." }),
};

const StatTile = ({ label, value, accent }) => (
  <Box
    sx={{
      textAlign: "center",
      py: 1.15,
      px: 0.5,
      borderRadius: "10px",
      bgcolor: accent ? BRAND.orangeLight : "#F1F5F9",
      border: `1px solid ${accent ? `${ORANGE}22` : BRAND.border}`,
    }}
  >
    <Typography sx={{ fontSize: "1.125rem", fontWeight: 800, color: NAVY, lineHeight: 1.1 }}>
      {value ?? "-"}
    </Typography>
    <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, fontWeight: 500, mt: 0.25 }}>
      {label}
    </Typography>
  </Box>
);

const EmployeeAttendanceMobileView = () => {
  const dispatch = useDispatch();
  const { userId } = getUserInfoFromToken();
  const { getMonthlyAttendance } = AttendanceVeiwController();
  const { getUserDetails } = AuthViewController();
  const { monthly_summary, error: attendanceError } = useSelector(
    (state) => state.TimeAttendance
  );
  const { userDetails } = useSelector((state) => state.auth);
  const monthlyAbortRef = useRef(null);

  const employeeId =
    userDetails?.id != null ? String(userDetails.id) : userId;

  const today = new Date();
  const currentYear = today.getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedDate, setSelectedDate] = useState(today);
  const [legendOpen, setLegendOpen] = useState(false);

  const loadError = attendanceError
    ? attendanceError.message || "Unable to load attendance. Please try again."
    : null;

  const currentDate = new Date(selectedYear, selectedMonth - 1);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  useEffect(() => {
    if (!userDetails?.id) {
      dispatch(getUserDetails());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userDetails?.id]);

  useEffect(() => {
    if (!employeeId) return undefined;

    if (monthlyAbortRef.current) {
      monthlyAbortRef.current.abort();
    }

    const controller = new AbortController();
    monthlyAbortRef.current = controller;

    dispatch(
      getMonthlyAttendance(employeeId, selectedYear, selectedMonth, {
        silent: true,
        signal: controller.signal,
      })
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, employeeId, selectedMonth, selectedYear]);

  const syncSelectedDate = (year, month, prevDate) => {
    const maxDay = new Date(year, month, 0).getDate();
    const day = Math.min(prevDate.getDate(), maxDay);
    setSelectedDate(new Date(year, month - 1, day));
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    syncSelectedDate(selectedYear, month, selectedDate);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    syncSelectedDate(year, selectedMonth, selectedDate);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const statusMap = useMemo(() => {
    const map = {};
    monthly_summary?.attendance_records?.forEach((record) => {
      map[record.date] = {
        code: record.status,
        detail: record.holiday_details,
      };
    });
    return map;
  }, [monthly_summary]);

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();
  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const dateKey = selectedDate.toLocaleDateString("en-CA");
  const statusEntry = statusMap[dateKey];
  const legendClass =
    legendData.find((l) => l.code === statusEntry?.code)?.className || "unknown";

  const attendanceRecord = monthly_summary?.attendance_records?.find(
    (record) => record.date === dateKey
  );

  const clockIn = attendanceRecord?.clock_in ?? null;
  const clockOut = attendanceRecord?.clock_out ?? null;
  const regularHours = attendanceRecord?.regular_hours ?? null;
  const overtimeHours = attendanceRecord?.overtime_hours ?? null;
  const sessions = attendanceRecord?.sessions || [];

  const shiftInfo = attendanceRecord?.shift ?? {
    name: "General",
    start_time: "09:00",
    end_time: "18:00"
  };

  const statusMessage = (() => {
    const code = statusEntry?.code;
    if (!code) {
      return { title: "No attendance record", sub: "No status logged for this date." };
    }
    const fn = STATUS_COPY[code];
    if (fn) return fn("", statusEntry?.detail);
    return { title: `Status: ${code}`, sub: "See legend for details." };
  })();

  const monthStats = [
    { label: "Present", value: monthly_summary?.present_days ?? "-", accent: false },
    { label: "Absent", value: monthly_summary?.absent_days ?? "-", accent: true },
    { label: "Leave", value: monthly_summary?.leave_days ?? "-", accent: false },
    { label: "Avg Hrs", value: monthly_summary?.average_work_hours ?? "-", accent: false },
  ];

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      const newYear = selectedYear - 1;
      setSelectedMonth(12);
      setSelectedYear(newYear);
      syncSelectedDate(newYear, 12, selectedDate);
    } else {
      const newMonth = selectedMonth - 1;
      setSelectedMonth(newMonth);
      syncSelectedDate(selectedYear, newMonth, selectedDate);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      const newYear = selectedYear + 1;
      setSelectedMonth(1);
      setSelectedYear(newYear);
      syncSelectedDate(newYear, 1, selectedDate);
    } else {
      const newMonth = selectedMonth + 1;
      setSelectedMonth(newMonth);
      syncSelectedDate(selectedYear, newMonth, selectedDate);
    }
  };

  const periodLabel = currentDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  return (
    <Box
      sx={{
        pb: "calc(72px + env(safe-area-inset-bottom, 0px))",
        pt: 0.5,
        px: 1.5,
        bgcolor: BG,
        minHeight: "100%",
      }}
      className="attendance-drawer-body-mobile"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: BRAND.orangeLight,
            color: ORANGE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 800, color: NAVY, lineHeight: 1.2 }}>
            My Attendance
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: BRAND.muted }}>
            View your monthly attendance record
          </Typography>
        </Box>
      </Box>

      <Box sx={{ ...cardSx, p: 1.25, mb: 1.25 }}>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            color: BRAND.muted,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 0.75,
          }}
        >
          Select Period
        </Typography>
        <MobileMonthYearFilters
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
          monthOptions={MONTH_OPTIONS}
          yearOptions={yearOptions}
          useShortMonthLabel={false}
        />
      </Box>

      {loadError && (
        <Box
          sx={{
            bgcolor: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: "10px",
            px: 1.5,
            py: 1,
            mb: 1.25,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", color: "#B91C1C" }}>
            {loadError}
          </Typography>
        </Box>
      )}

      <Grid container spacing={1} sx={{ mb: 1.25 }}>
        {monthStats.map((item) => (
          <Grid item xs={3} key={item.label}>
            <StatTile label={item.label} value={item.value} accent={item.accent} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ ...cardSx, p: 1.25, mb: 1.25 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.25,
          }}
        >
          <IconButton
            size="small"
            onClick={handlePrevMonth}
            aria-label="Previous month"
            sx={{
              border: `1px solid ${BRAND.border}`,
              color: NAVY,
              borderRadius: "10px",
              width: 34,
              height: 34,
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.9375rem" }}>
            {periodLabel}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextMonth}
            aria-label="Next month"
            sx={{
              border: `1px solid ${BRAND.border}`,
              color: NAVY,
              borderRadius: "10px",
              width: 34,
              height: 34,
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        <div className="attendance-calendar-grid">
          {["S", "M", "T", "W", "T", "F", "S"].map((day, idx) => (
            <div className="attendance-calendar-weekday" key={`${day}-${idx}`}>
              {day}
            </div>
          ))}

          {Array(getFirstDayOfMonth(currentDate.getMonth(), currentDate.getFullYear()))
            .fill(null)
            .map((_, i) => (
              <div key={`empty-${i}`} className="attendance-calendar-cell empty" />
            ))}

          {Array(daysInMonth(currentDate.getMonth(), currentDate.getFullYear()))
            .fill(null)
            .map((_, i) => {
              const date = i + 1;
              const cellDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                date
              );
              const fullDate = cellDate.toLocaleDateString("en-CA");
              const status = statusMap[fullDate];
              const cellClass =
                legendData.find((l) => l.code === status?.code)?.className || "";

              return (
                <div
                  key={date}
                  className={[
                    "attendance-calendar-cell",
                    cellClass,
                    isSameDay(cellDate, selectedDate) ? "is-selected" : "",
                    isSameDay(cellDate, today) ? "is-today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setSelectedDate(cellDate)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setSelectedDate(cellDate);
                  }}
                >
                  <span className="attendance-calendar-date">{date}</span>
                  {status?.code && (
                    <span
                      className={`attendance-calendar-code legend-symbol ${cellClass}`}
                    >
                      {status.code}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      </Box>

      <Box sx={{ ...cardSx, p: 1.25, mb: 1.25 }}>
        <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.9375rem", mb: 1.25 }}>
          Day Details
        </Typography>
        <div className="attendance-day-summary">
          <div className="attendance-day-date-block">
            <div className="attendance-day-date-num">{selectedDate.getDate()}</div>
            <div className="attendance-day-date-weekday">
              {selectedDate.toLocaleString("en-us", { weekday: "short" })}
            </div>
          </div>
          <div>
            <p className="attendance-day-meta-title">{shiftInfo.name}</p>
            <p className="attendance-day-meta-sub">Shift: {shiftInfo.start_time} to {shiftInfo.end_time}</p>
          </div>
        </div>

        <div className={`attendance-status-banner ${legendClass}`}>
          <strong>{statusMessage.title}</strong>
          <span>{statusMessage.sub}</span>
        </div>

        {clockIn ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <div className="attendance-clock-grid">
              <div className="attendance-clock-item">
                <p className="attendance-clock-item-label">Clock In</p>
                <p className="attendance-clock-item-value">{formatTime(clockIn)}</p>
              </div>
              <div className="attendance-clock-item">
                <p className="attendance-clock-item-label">Clock Out</p>
                <p className="attendance-clock-item-value">
                  {clockOut ? formatTime(clockOut) : "-"}
                </p>
              </div>
              <div className="attendance-clock-item">
                <p className="attendance-clock-item-label">Regular Hrs</p>
                <p className="attendance-clock-item-value">{regularHours ?? "-"}</p>
              </div>
              <div className="attendance-clock-item">
                <p className="attendance-clock-item-label">Overtime Hrs</p>
                <p className="attendance-clock-item-value">{overtimeHours ?? "-"}</p>
              </div>
            </div>
          </>
        ) : null}

        {/* Punch Log Sessions */}
        {sessions.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: NAVY, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>🕐</span> Punch Log
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', bgcolor: 'rgba(59,130,246,0.1)', color: '#2563eb', px: 1, py: 0.25, borderRadius: 3, fontWeight: 600 }}>
                {sessions.length} session{sessions.length > 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sessions.map((s, idx) => {
                const isOpen = s.clock_in && !s.clock_out;
                return (
                  <Box key={idx} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.78rem'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%',
                        bgcolor: isOpen ? '#fff3e0' : '#e8f5e9',
                        color: isOpen ? '#e65100' : '#2e7d32',
                        border: `1px solid ${isOpen ? '#ff9800' : '#4caf50'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '0.68rem', flexShrink: 0
                      }}>
                        {idx + 1}
                      </Box>
                      <span>
                        <strong>In:</strong> {s.clock_in ? formatTime(s.clock_in) : '-'}
                      </span>
                      <span style={{ color: '#cbd5e1' }}>|</span>
                      <span>
                        <strong>Out:</strong> {s.clock_out ? formatTime(s.clock_out) : <span style={{ color: '#ff9800', fontWeight: 600 }}>⏳ On Duty</span>}
                      </span>
                    </Box>
                    {s.duration_hours != null && (
                      <Typography component="span" sx={{ fontWeight: 600, color: '#2563eb', bgcolor: '#eff6ff', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>
                        {s.duration_hours}h
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ ...cardSx, overflow: "hidden", mb: 1 }}>
        <button
          type="button"
          className="attendance-legend-toggle"
          onClick={() => setLegendOpen((prev) => !prev)}
          aria-expanded={legendOpen}
          style={{ width: "100%", padding: "12px 16px" }}
        >
          <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.9375rem" }}>
            Status Legend
          </Typography>
          <ExpandMoreIcon
            sx={{
              color: BRAND.muted,
              transform: legendOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </button>
        <Collapse in={legendOpen}>
          <Box sx={{ px: 1.25, pb: 1.25 }}>
            <div className="attendance-legend-grid">
              {legendData.map((item) => (
                <div className="attendance-legend-item" key={item.code}>
                  <span className={`legend-symbol ${item.className}`}>{item.code}</span>
                  <span className="attendance-legend-label">{item.label}</span>
                </div>
              ))}
            </div>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default EmployeeAttendanceMobileView;
